import { Component, Input, OnInit } from '@angular/core';
import { OnyxEvent } from '../../models/event';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { ReservationCompare } from '../../models/reservation-compare';
import { ReservationCompareStore } from '../../service/reservation-compare-store';
import { LogService } from '../../../shared/log/log.service';
import { NumberUtils } from '../../../shared/utils/number.util';
import { ReservationStatusCode } from '../../models/reservation-status-code.enum';
import { ReservationUtils } from '../../utils/reservation-utils';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'pioneer-reservation-summary-table',
  templateUrl: './reservation-summary-table.component.html'
})
export class ReservationSummaryTableComponent implements OnInit {

  onyxEvent: OnyxEvent;

  public reservations: Observable<List<ReservationCompare>>;

  public statusCodes: Observable<ReservationStatusCode[]>;

  public filteredClass = '';

  constructor(private reservationCompareStore: ReservationCompareStore,
              private translateService: TranslateService,
              private logService: LogService) {
  }

  @Input('event')
  set event(event: OnyxEvent) {
    this.onyxEvent = event;
  }

  ngOnInit() {
    this.statusCodes = this.reservationCompareStore.getActiveStatusCodes();
    this.reservations = this.reservationCompareStore.getReservationList()
      .map(reservationList => ReservationUtils.onlyReservationsFilter(reservationList));

    this.isFiltered().subscribe(isFiltered => {
      if (isFiltered) {
        this.filteredClass = 'filtered';
      } else {
        this.filteredClass = '';
      }
    });
  }

  getReservations(): Observable<List<ReservationCompare>> {
    return this.reservations;
  }

  getRsvps(): Observable<List<ReservationCompare>> {
    return this.reservationCompareStore.getReservationList()
      .map(reservationList => ReservationUtils.onlyRsvpFilter(reservationList));
  }

  getFilteredReservations(): Observable<List<ReservationCompare>> {
    return this.reservationCompareStore.getCurrentFilteredReservations()
      .map(reservationList => ReservationUtils.onlyReservationsFilter(reservationList));
  }

  getReservationRevenueTotal(): Observable<number> {
    return ReservationUtils.calculateRevenueTotal(this.getReservations());
  }

  getFilteredReservationRevenueTotal(): Observable<number> {
    return ReservationUtils.calculateRevenueTotal(this.getFilteredReservations());
  }

  getReservationCommissionTotal(): Observable<number> {
    return ReservationUtils.calculateCommissionTotal(this.getReservations());
  }

  getRsvpCommissionTotal(): Observable<number> {
    return ReservationUtils.calculateRsvpCommissionTotal(this.getRsvps());
  }

  getFilteredReservationCommissionTotal(): Observable<number> {
    return ReservationUtils.calculateCommissionTotal(this.getFilteredReservations());
  }

  getFilteredReservationNightsTotal(): Observable<number> {
    return ReservationUtils.calculateReservationTotalNights(this.getFilteredReservations());
  }

  isReservationListEmpty(): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getReservations());
  }

  isRsvpListEmpty(): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getRsvps());
  }

  isFilteredListEmpty(): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getFilteredReservations());
  }

  isFiltered(): Observable<boolean> {
    const filteredSizeObs = ReservationUtils.getListSize(this.getFilteredReservations());
    const unfilteredSizeObs = ReservationUtils.getListSize(this.getReservations());

    return filteredSizeObs.flatMap(filteredSize => {
      return unfilteredSizeObs.map(unfilteredSize => {
        return filteredSize !== unfilteredSize;
      });
    });
  }

  getCommissionDelta(): Observable<number> {
    const reservationCommissionObj = this.getReservationCommissionTotal();
    const rsvpCommissionObj = this.getRsvpCommissionTotal();

    return reservationCommissionObj.flatMap(reservationCommission => {
      return rsvpCommissionObj.map(rsvpCommission => {
        return reservationCommission - rsvpCommission;
      });
    }).map(total => NumberUtils.round2Fixed(total));
  }

  getCommissionDeltaText(): Observable<string> {
    return this.getCommissionDelta().flatMap(delta => {
      if (delta > 0) {
        return this.translateService.get('EVENT.RESERVATION.TOOLTIP_COMMISSION_DIFFERENCE_MORE');
      } else if (delta < 0) {
        return this.translateService.get('EVENT.RESERVATION.TOOLTIP_COMMISSION_DIFFERENCE_LESS');
      } else {
        return this.translateService.get('EVENT.RESERVATION.TOOLTIP_COMMISSION_DIFFERENCE_EQUAL');
      }
    });
  }

  hasFilteredOutResults(): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getFilteredReservations()).flatMap(filteredListIsEmpty => {
      return this.isReservationListEmpty().map(isReservationListEmpty => filteredListIsEmpty !== isReservationListEmpty);
    });
  }

  resetStatusCodes(): void {
    this.reservationCompareStore.resetStatusCodes();
  }

  totalCommissionClass(): Observable<string> {
    return this.reservationCompareStore.areRsvpsPresent().flatMap(hasRsvps => {
      return this.getCommissionDelta().map( total => {
        return hasRsvps && total > 0 ? 'commission-up' : hasRsvps && total < 0 ? 'commission-down' : '';
      } );
    });
  }

  getStatusClass(compare: ReservationCompare): string {
    if (compare.reservation) {
      switch (compare.reservation.status) {
        case 'R':
          return 'res-status-reserved';
        case 'I':
          return 'res-status-checked-in';
        case 'O':
          return 'res-status-checked-out';
        case 'C':
          return 'res-status-cancelled';
        case 'N':
          return 'res-status-no-show';
        default:
          return '';
      }
    }
    return '';
  }

  isCommissionUp(compare: ReservationCompare): boolean {
    return compare.rsvp && compare.delta && compare.delta.commission > 0;
  }

  isCommissionDown(compare: ReservationCompare): boolean {
    return compare.rsvp && compare.delta && compare.delta.commission < 0;
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/event/reservations/reservation-summary-table/reservation-summary-table.component.ts